import { Box, useMediaQuery, useTheme } from '@mui/material';
import React, { FC, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useFooterContext, useFooterVisibility } from '../../../contexts/FooterContext';
import { useScrollGuidance } from '../../../hooks/useScrollGuidance';
import { selectFooterBackgroundColor } from '../../../store/selectors/derived';
import { selectIsEmbed } from '../../../store/selectors/init';
import { AppWrapper } from '../_layouts';

// Sticky bottom action bar. Visibility is driven by the shared `useScrollGuidance` state
// machine against the anchor the current page registers via `useFooter`.
export const Footer: FC = () => {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { footerState, anchorRef } = useFooterContext();
  const { setFooterVisible } = useFooterVisibility();

  const footerBgColorOverride = useSelector(selectFooterBackgroundColor);
  const isEmbed = useSelector(selectIsEmbed);

  const { barVisible } = useScrollGuidance({ anchorRef });

  // Sync visibility so the FAB (which rides above the bar) can read it without subscribing
  // to useScrollGuidance itself.
  useEffect(() => {
    setFooterVisible(barVisible);
  }, [barVisible, setFooterVisible]);

  const footerBgColor = useMemo(() => {
    if (footerBgColorOverride) {
      return footerBgColorOverride;
    }

    return mobile ? theme.palette.grey[100] : theme.palette.common.white;
  }, [footerBgColorOverride, mobile, theme]);

  const footerStyles = useMemo(
    () => ({
      position: 'fixed' as const,
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: footerBgColor,
      boxShadow: theme.shadows[8],
      zIndex: 20,
      transform: barVisible ? 'translate(0, 0)' : 'translate(0, 110%)',
      transition: 'transform 300ms cubic-bezier(0.32, 0.72, 0, 1)',
      pointerEvents: barVisible ? ('auto' as const) : ('none' as const),
      ...(isEmbed ? { pointerEvents: 'none' as const } : {}),
    }),
    [footerBgColor, barVisible, isEmbed, theme],
  );

  const shouldShowBackButton = useMemo(() => {
    return !!footerState?.backButton && !mobile;
  }, [footerState, mobile]);

  if (!footerState) {
    return null;
  }

  return (
    <Box data-testid="footer-bar" sx={footerStyles}>
      <AppWrapper
        sx={{
          flexDirection: 'row',
          display: 'flex',
          justifyContent: mobile ? 'stretch' : 'flex-end',
          alignItems: 'center',
          paddingBlock: mobile ? 1.5 : 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: mobile ? 'column' : 'row',
            justifyContent: shouldShowBackButton ? 'space-between' : 'flex-end',
            alignItems: mobile ? 'stretch' : 'center',
            gap: mobile ? 1.5 : 2,
            width: '100%',
          }}
        >
          {shouldShowBackButton && footerState.backButton}
          <Box
            sx={{
              display: 'flex',
              flexDirection: mobile ? 'column' : 'row',
              alignItems: mobile ? 'stretch' : 'center',
              gap: mobile ? 1.5 : 2,
              width: mobile ? '100%' : 'auto',
              // Enforce the larger full-width mobile CTA here so every page's footer button
              // gets it from one place.
              ...(mobile && {
                '& > .MuiButton-root': {
                  minHeight: 42,
                  fontSize: 15,
                  lineHeight: '26px',
                },
              }),
            }}
          >
            {footerState.helperText && (
              <Box sx={{ ...(mobile && { alignSelf: 'center', textAlign: 'center' }) }}>{footerState.helperText}</Box>
            )}
            {footerState.submitButton}
          </Box>
        </Box>
      </AppWrapper>
    </Box>
  );
};
